import { useFormikContext } from 'formik'
import React, { ChangeEventHandler, useState } from 'react'
import { Item } from '@react-stately/collections'
import cx from 'classnames'

import Styles from '../ReviewTemplateForm.module.scss'
import { TemplateFormData } from 'components/ReviewTemplateFormModal/Types'
import { Select } from 'components/Select/Select'
import { TextInputField } from 'components/Form/TextInputField'

interface Props {
  isDisabled: boolean
}

const numberOptions = ['3', '5', '7'] as const
type NumberOption = (typeof numberOptions)[number]

const dayOptions = [...numberOptions, 'custom'] as const
type DayOption = (typeof dayOptions)[number]

const isNumberOption = (x: unknown): x is NumberOption => numberOptions.includes(`${x}` as NumberOption)
const isDayOption = (x: unknown): x is DayOption => isNumberOption(x) || x === 'custom'

export const DueDate = ({ isDisabled }: Props) => {
  const form = useFormikContext<TemplateFormData>()
  const [selectedKey, setSelectedKey] = useState<DayOption>(
    isNumberOption(form.values.days_ahead) ? (`${form.values.days_ahead}` as NumberOption) : 'custom',
  )

  const handleSelectionChange = (key: React.Key) => {
    if (isDayOption(key)) setSelectedKey(key)
    if (isNumberOption(key)) {
      form.setFieldValue('days_ahead', Number(key))
    }
  }

  const handleCustomInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (e.target.value.length > 3) return
    const parsed = Number(e.target.value)
    if (!Number.isInteger(parsed)) return

    form.setFieldValue('days_ahead', parsed)
  }

  const customInputCharLengthClassName = {
    1: Styles.oneChar,
    2: Styles.twoChars,
    3: Styles.threeChars,
  }[form.values.days_ahead.toString().length]

  return (
    <>
      <label className={Styles.label} htmlFor="due-date">
        Due After
      </label>
      <div className={Styles.dueDateInputsWrapper}>
        <Select
          isDisabled={isDisabled}
          variant="secondary"
          size="lg"
          selectedKey={selectedKey}
          onSelectionChange={handleSelectionChange}
          id="due-date"
          aria-label="Due After"
        >
          {dayOptions.map((numberDays) => (
            <Item key={numberDays} textValue={numberDays.toString()}>
              {numberDays === 'custom' ? 'Custom' : `${numberDays} days`}
            </Item>
          ))}
        </Select>

        {selectedKey === 'custom' && (
          <>
            <TextInputField
              id="custom-due-date"
              aria-label="Number of Days"
              size="lg"
              onChange={handleCustomInputChange}
              value={form.values.days_ahead.toString()}
              placeholder="Number of days before the review ends"
              errorMessage={form.errors.days_ahead}
              className={cx(Styles.customDueDateInput, customInputCharLengthClassName)}
            />
          </>
        )}
      </div>
    </>
  )
}
